import * as m from '@/paraglide/messages';
import {
  fieldSurface,
  menuSurface,
} from '@/components/web-fe/design-v2/surfaces';
import type { Voice } from '@listening/api-contract/legacy-client-types';
import { cn, useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import CheckCircleSmallIcon from '@listening/assets/icons/circleCheck.svg?react';
import FilterIcon from '@listening/assets/icons/filter.svg?react';
import PauseCircleSmallIcon from '@listening/assets/icons/pause-circle-small.svg?react';
import PlayCircleSmallIcon from '@listening/assets/icons/play-circle-small.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '@listening/frontend/components/radix/dropdown-menu';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import {
  VoiceListSkeleton,
  VoicePaneSkeleton,
} from '@/components/web-fe/design-v2/skeletons';
import { createFileRoute } from '@tanstack/react-router';
import {
  type ComponentProps,
  createContext,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import WaveSurfer from 'wavesurfer.js';
import { FilterVoicesDropdown } from '@/components/web-fe/dialogs/filter-voices-dialog';
import { usePremiumDialog } from '@/components/web-fe/misc/providers/premium-dialog-provider';
import { Switch } from '@listening/frontend/components/radix/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@listening/frontend/components/radix/select';
import {
  speedRateKey,
  speedRateLabel,
  speedRateOptions,
} from '@listening/frontend/play-back-speed-presets';
import { FREE_SPEED_LIMIT } from '@/components/start/read-along/playback-speed-dropdown';
import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import { useAudioChunkPlayer } from '@/lib/audio-chunk-player';
import { SectionLabel } from '@/components/web-fe/design-v2/section-label';
import {
  SettingRow,
  SettingsCard,
  SettingsPaneHeader,
} from '@/components/web-fe/design-v2/settings-shell';
import { useAtom, useAtomValue } from '@effect/atom-react';
import { updateUserPreferencesAtom } from '@listening/frontend/query/user-atoms';
import { voiceCatalogAtom, voicesAtom } from '@listening/frontend/query/voices';
import { Exit } from 'effect';
import { AsyncResult } from 'effect/reactivity';
import { voiceLangToLangLabel } from '@/lib/utils';
import { useIsInvalidSubscription } from '@/services/subscription-service';
import { useUserPreferences } from '@/services/user-service';

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/settings/voice',
)({
  component: Page,
  pendingComponent: VoicePaneSkeleton,
  pendingMs: 0,
});

const WaveSurferRegistryContext = createContext<RefObject<
  Array<() => void>
> | null>(null);

function Page() {
  const registryRef = useRef<Array<() => void>>([]);
  const [genderFilter, setGenderFilter] = useState<Voice['gender'] | null>(
    null,
  );
  const [softnessFilter, setSoftnessFilter] = useState<
    Array<Voice['softness']>
  >([]);
  const genderFilterState: [
    Voice['gender'] | null,
    React.Dispatch<React.SetStateAction<Voice['gender'] | null>>,
  ] = [genderFilter, setGenderFilter];
  const softnessFilterState: [
    Array<Voice['softness']>,
    React.Dispatch<React.SetStateAction<Array<Voice['softness']>>>,
  ] = [softnessFilter, setSoftnessFilter];

  const voiceOptions = useAtomValue(voiceCatalogAtom);
  const voicesPending = AsyncResult.isInitial(useAtomValue(voicesAtom));

  const freeVoices = voiceOptions.filter((v) => v.is_free);
  const premiumVoices = voiceOptions.filter((v) => !v.is_free);

  const filterVoiceFn = (v: Voice) => {
    if (genderFilterState[0] && v.gender !== genderFilterState[0]) return false;
    if (
      softnessFilterState[0].length > 0 &&
      !softnessFilterState[0].includes(v.softness)
    )
      return false;
    return true;
  };

  return (
    <>
      <div className='flex items-start justify-between gap-[var(--v2-gap-3)]'>
        <SettingsPaneHeader
          title={m.settings_v2_preferred_voice()}
          description={m.settings_v2_preferred_voice_description()}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <TooltipButton
              className='h-auto shrink-0 rounded-full p-1'
              toolTipContent={m.common_filter_voices()}
              variant='ghost'
            >
              <FilterIcon className='size-6' />
            </TooltipButton>
          </DropdownMenuTrigger>
          <FilterVoicesDropdown
            genderFilterState={genderFilterState}
            softnessFilterState={softnessFilterState}
          />
        </DropdownMenu>
      </div>

      {voicesPending ? (
        <VoiceListSkeleton />
      ) : (
        <WaveSurferRegistryContext value={registryRef}>
          <VoiceGroup
            label={m.settings_free_voices()}
            options={freeVoices.filter(filterVoiceFn)}
          />
          <VoiceGroup
            isPremium
            label={m.settings_premium_voices()}
            options={premiumVoices
              .filter(filterVoiceFn)
              .map((v) => ({ ...v, isPremium: true }))}
          />
        </WaveSurferRegistryContext>
      )}

      <SettingsPaneHeader title={m.settings_v2_playback()} />
      <PlaybackCard />
    </>
  );
}

/**
 * The playback preferences that actually exist: `playbackRate` and `autoScroll`
 * from the audio-preferences store. Both are device-local — neither is part of
 * the server-side `UpdatePreferencesRequest`, which carries only `voice` and
 * `most_recent_language`.
 */
function PlaybackCard() {
  const playbackRate = useAudioPrefsStore((state) => state.playbackRate);
  const setPlaybackRate = useAudioPrefsStore((state) => state.setPlaybackRate);
  // The store is shared with the player, so writing an unentitled rate here
  // would persist it and take effect on the next load — the same gate the
  // reader's speed control applies has to hold on this surface too.
  const isInvalidSubscription = useIsInvalidSubscription();
  const { setCurrentPremiumDialog } = usePremiumDialog();
  // Persisting alone leaves the running audio element at the old rate until the
  // next chunk loads, which then jumps. The reader's control sets both.
  const { player } = useAudioChunkPlayer();
  const autoScroll = useAudioPrefsStore((state) => state.autoScroll);
  const setAutoScroll = useAudioPrefsStore((state) => state.setAutoScroll);

  return (
    <SettingsCard>
      <SettingRow
        title={m.settings_v2_default_speed()}
        description={m.settings_v2_default_speed_description()}
      >
        <Select
          value={speedRateKey(playbackRate)}
          onValueChange={(next) => {
            const rate = Number(next);
            if (rate > FREE_SPEED_LIMIT && isInvalidSubscription) {
              setCurrentPremiumDialog('playback_speed');
              return;
            }
            setPlaybackRate(rate);
            player.setRate(rate);
          }}
        >
          <SelectTrigger
            className={cn('h-9 w-[140px] text-[13px]', fieldSurface)}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={menuSurface}>
            {/* A rate set from the reader is usually not a preset, so it is
                listed alongside them rather than left unrepresented — the
                trigger renders blank when no item matches `value`. */}
            {speedRateOptions(playbackRate).map((rate) => (
              <SelectItem key={speedRateKey(rate)} value={speedRateKey(rate)}>
                {speedRateLabel(rate)}×
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow
        title={m.settings_v2_auto_scroll()}
        description={m.settings_v2_auto_scroll_description()}
      >
        <Switch checked={autoScroll} onCheckedChange={setAutoScroll} />
      </SettingRow>
    </SettingsCard>
  );
}

function VoiceGroup({
  label,
  options,
  isPremium,
}: {
  label: string;
  options: Array<ComponentProps<typeof VoiceItem>>;
  isPremium?: boolean;
}) {
  return (
    <div className='flex flex-col gap-2'>
      <SectionLabel>{label}</SectionLabel>
      <div className='flex flex-col'>
        {options.map((option) => (
          <VoiceItem key={option.name} {...option} isPremium={isPremium} />
        ))}
      </div>
    </div>
  );
}

const useWavSurfer = (
  audioSrc: string,
  containerRef: RefObject<HTMLDivElement | null>,
) => {
  const registryRef = useContextAndErrorIfNull(WaveSurferRegistryContext);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastPlayed, setLastPlayed] = useState(false);

  const pauseCallback = useCallback(() => {
    waveSurferRef.current?.pause();
    setIsPlaying(false);
    setLastPlayed(false);
  }, []);

  const playPause = () => {
    for (const pause of registryRef.current) {
      if (pauseCallback !== pause) {
        pause();
      }
    }
    setLastPlayed(true);
    void waveSurferRef.current?.playPause();
    setIsPlaying(waveSurferRef.current?.isPlaying() ?? false);
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const waveSurfer = WaveSurfer.create({
      container: containerRef.current,
      height: 14,
      width: 240,
      url: audioSrc,
      progressColor: '#7CD4FD',
      barGap: 3,
      barWidth: 3,
      barRadius: 10,
      barHeight: 0.9,
      normalize: true,
      waveColor: '#D9D9D9',
      cursorColor: 'transparent',
      interact: false,
    });
    waveSurfer.on('ready', () => {
      waveSurferRef.current = waveSurfer;
    });

    waveSurfer.on('finish', () => {
      setIsPlaying(false);
    });

    const registry = registryRef.current;
    registry.push(pauseCallback);

    return () => {
      registry.splice(registry.indexOf(pauseCallback), 1);
      waveSurfer.destroy();
    };
  }, [audioSrc, containerRef, pauseCallback, registryRef]);

  return { isPlaying, playPause, lastPlayed };
};

function VoiceItem({
  name,
  image_url: imgSrc,
  sample_url: audioSrc,
  isPremium,
  gender,
  softness,
  lang,
}: {
  isPremium?: boolean | undefined;
} & Voice) {
  const value = name.toUpperCase();
  const tags = [
    gender === 'male' ? m.settings_voice_male() : m.settings_voice_female(),
    softness === 'moderate'
      ? m.settings_voice_moderate()
      : softness === 'soft'
        ? m.settings_voice_soft()
        : m.settings_voice_deep(),
    lang !== 'en' ? voiceLangToLangLabel(lang) : undefined,
  ].filter(Boolean);
  const [preferencesUpdate, updatePreferences] = useAtom(
    updateUserPreferencesAtom,
    { mode: 'promiseExit' },
  );

  const serverVoice = AsyncResult.getOrElse(
    useUserPreferences(),
    () => undefined,
  )?.voice;
  const isSelected = serverVoice?.toUpperCase() === value;
  const containerRef = useRef<HTMLDivElement>(null);
  const { isPlaying, playPause, lastPlayed } = useWavSurfer(
    audioSrc,
    containerRef,
  );

  const isInvalidSubscription = useIsInvalidSubscription();
  const { setCurrentPremiumDialog } = usePremiumDialog();

  return (
    <div className='relative flex w-full items-center overflow-hidden rounded-none border-[length:var(--v2-border-width)] border-b-0 border-[var(--v2-card-border)] first:rounded-t-[var(--v2-card-radius)] last:rounded-b-[var(--v2-card-radius)] last:border-b-[length:var(--v2-border-width)]'>
      <Button
        className='flex h-auto w-full items-center justify-between rounded-none bg-[var(--v2-page-bg)] p-4 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
        onClick={playPause}
        variant='ghost'
      >
        <div className='flex items-center gap-3'>
          <img alt={name} className='size-10 rounded-full' src={imgSrc} />
          <div className='flex flex-col items-start gap-1'>
            <div className='flex gap-4'>
              <span className='text-[var(--v2-text-primary)]'>{name}</span>
              <div
                className={cn(
                  'opacity-0 transition-all',
                  lastPlayed && 'opacity-100',
                )}
                ref={containerRef}
              />
            </div>
            <span className='text-xs text-[var(--v2-text-muted)]'>
              {tags.join(' • ')}
            </span>
          </div>
        </div>
        <div className='flex items-center gap-4'>
          {isSelected && <CheckCircleSmallIcon className='size-5' />}
          {isPlaying ? (
            <PauseCircleSmallIcon className='size-6' />
          ) : (
            <PlayCircleSmallIcon className='size-6 text-[var(--v2-text-muted)]' />
          )}
        </div>
      </Button>
      {lastPlayed && !isSelected && (
        <Button
          className='absolute right-12 h-7 rounded-lg bg-[var(--v2-accent)]/10 text-sm font-medium text-[var(--v2-accent)]'
          onClick={() => {
            if (preferencesUpdate.waiting) return;
            if (isInvalidSubscription && isPremium) {
              setCurrentPremiumDialog('voice');
              return;
            }
            void updatePreferences({ voice: value }).then((exit) => {
              if (Exit.isSuccess(exit))
                toast.success(m.settings_voice_selected_toast());
            });
          }}
        >
          {m.settings_voice_select()}
        </Button>
      )}
    </div>
  );
}
